import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { brainApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Upload, Loader2, CheckCircle2, Circle, AlertCircle, RefreshCw, Sparkles,
} from "lucide-react";

import {
  computeWizardStep,
  clearProgress,
  logWizardState,
} from "@/components/brain/wizardState";
import {
  FILE_UPLOAD_ACCEPT,
  FILE_UPLOAD_FORMATS_LABEL,
  FILE_UPLOAD_REJECT_MESSAGE,
  partitionKnowledgeUploadFiles,
  knowledgeUploadRejectToast,
} from "@/lib/knowledgeFileTypes";

const ACCEPT = FILE_UPLOAD_ACCEPT;

const STEPS = [
  { id: 1, label: "Create" },
  { id: 2, label: "Upload" },
  { id: 3, label: "Process" },
];

const sourceDetailPropType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  document_count: PropTypes.number,
  chunk_count: PropTypes.number,
  openclaw_sync_status: PropTypes.string,
  metadata: PropTypes.object,
  assignments: PropTypes.arrayOf(PropTypes.string),
});

const processingFlagsPropType = PropTypes.shape({
  uploaded: PropTypes.bool,
  chunked: PropTypes.bool,
  embedded: PropTypes.bool,
  indexed: PropTypes.bool,
  synced: PropTypes.bool,
});

const syncResultPropType = PropTypes.shape({
  status: PropTypes.string,
  reason: PropTypes.string,
  sync_mode: PropTypes.string,
});

const uploadResultPropType = PropTypes.shape({
  status: PropTypes.string,
  error: PropTypes.string,
});

const customSourceSummaryPropType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  document_count: PropTypes.number,
  chunk_count: PropTypes.number,
  status: PropTypes.string,
  openclaw_sync_status: PropTypes.string,
});

function progressStepBarClass(step, stepId) {
  if (step > stepId) return "bg-indigo-600";
  if (step === stepId) return "bg-indigo-400";
  return "bg-slate-200";
}

function logWizard(event, detail) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info(`[brain-wizard] ${event}`, detail ?? "");
  }
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function describeSync(sync, sourceDetail) {
  if (!sync && sourceDetail?.chunk_count > 0) {
    return { label: "Knowledge indexed in Sellyx Brain", hint: "Available to all agents at reply time.", ok: true };
  }
  if (!sync) {
    return { label: "OpenClaw sync pending", hint: null, ok: false };
  }
  const st = sync.status;
  if (st === "completed") {
    if (sync.sync_mode === "soul_embed") {
      return { label: "Knowledge synced to agent workspace", hint: "Processed chunks embedded in SOUL.md.", ok: true };
    }
    return {
      label: "Knowledge indexed and ready for all agents",
      hint: sync.reason || "Available via Sellyx Brain search at reply time.",
      ok: true,
    };
  }
  if (st === "skipped") {
    return {
      label: "Knowledge indexed — gateway sync deferred",
      hint: sync.reason || "Gateway offline; workspace sync runs when connected.",
      ok: true,
    };
  }
  if (st === "failed") {
    return {
      label: "Sync needs attention",
      hint: sync.reason || "Knowledge is still indexed in Sellyx — retry sync.",
      ok: false,
    };
  }
  return { label: syncStatusLabel(st), hint: sync.reason, ok: st === "completed" || st === "skipped" };
}

function syncStatusLabel(status) {
  const map = {
    completed: "Knowledge ready for all agents",
    skipped: "Indexed in Sellyx — gateway sync deferred",
    failed: "Sync needs retry",
    running: "Syncing knowledge…",
    pending: "Sync pending",
  };
  return map[status] || status;
}

function ProgressHeader({ step, showSuccess }) {
  if (showSuccess) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-emerald-700 font-medium">
        <CheckCircle2 size={16} /> Setup complete
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span>Step {Math.min(step, 3)} of 3</span>
        <span>{STEPS[Math.min(step, 3) - 1]?.label}</span>
      </div>
      <div className="flex gap-1">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              progressStepBarClass(step, s.id),
            )}
          />
        ))}
      </div>
      <div className="flex justify-between gap-1 text-[10px] text-slate-400 overflow-hidden">
        {STEPS.map((s) => (
          <span key={s.id} className={cn("truncate", step >= s.id && "text-indigo-600 font-medium")}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

ProgressHeader.propTypes = {
  step: PropTypes.number.isRequired,
  showSuccess: PropTypes.bool.isRequired,
};

function StepIcon({ done, active }) {
  if (done) {
    return <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />;
  }
  if (active) {
    return <Loader2 size={18} className="animate-spin text-indigo-600 shrink-0 mt-0.5" />;
  }
  return <Circle size={18} className="text-slate-300 shrink-0 mt-0.5" />;
}

StepIcon.propTypes = {
  done: PropTypes.bool,
  active: PropTypes.bool,
};

function StepIndicator({ done, active, label, hint }) {
  return (
    <div className="flex items-start gap-3">
      <StepIcon done={done} active={active} />
      <div>
        <div className={cn("text-[13px] font-medium", done ? "text-slate-900" : "text-slate-700")}>{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

StepIndicator.propTypes = {
  done: PropTypes.bool,
  active: PropTypes.bool,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

function buildProcessingFlags(detail) {
  const docCount = detail.document_count ?? 0;
  const chunkCount = detail.chunk_count ?? 0;
  const hasKnowledge = docCount > 0 && chunkCount > 0;
  const syncOk = detail.openclaw_sync_status === "completed"
    || detail.openclaw_sync_status === "skipped";
  return {
    uploaded: docCount > 0,
    chunked: chunkCount > 0,
    embedded: chunkCount > 0,
    indexed: chunkCount > 0,
    synced: hasKnowledge && syncOk,
  };
}

function buildSyncResultFromDetail(detail) {
  const docCount = detail.document_count ?? 0;
  const chunkCount = detail.chunk_count ?? 0;
  if (docCount === 0 || chunkCount === 0) return null;
  const skipped = detail.openclaw_sync_status === "skipped";
  return {
    status: detail.openclaw_sync_status || "pending",
    reason: skipped ? "OpenClaw gateway offline — knowledge indexed in Sellyx." : undefined,
  };
}

function WizardStepCreate({ name, setName, description, setDescription }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-[12px]">Datasource name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Company Knowledge Base"
          className="mt-1"
          data-testid="wizard-source-name"
        />
      </div>
      <div>
        <Label className="text-[12px]">Description (optional)</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Product docs, FAQs, pricing sheets…"
          className="mt-1 min-h-[72px] text-[13px]"
        />
      </div>
    </div>
  );
}

WizardStepCreate.propTypes = {
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  setDescription: PropTypes.func.isRequired,
};

function applySelectedUploadFiles(rawFiles, setFiles, setError) {
  const { accepted, rejected } = partitionKnowledgeUploadFiles(rawFiles);
  if (rejected.length) {
    setError?.(knowledgeUploadRejectToast(rejected));
  } else {
    setError?.(null);
  }
  setFiles(accepted);
  return accepted;
}

function WizardStepUpload({
  name, sourceDetail, dragOver, setDragOver, setFiles, setError, files, fileRef, uploadResults,
}) {
  return (
    <div className="space-y-3">
      <div className="text-[12px] text-slate-600 min-w-0">
        Source:{" "}
        <strong className="text-slate-900 break-all" title={(name || sourceDetail?.name) || undefined}>
          {name || sourceDetail?.name}
        </strong>
      </div>
      <button
        type="button"
        className={cn(
          "rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer w-full",
          dragOver ? "border-indigo-400 bg-indigo-50/40" : "border-slate-200 bg-slate-50/40",
        )}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          applySelectedUploadFiles(e.dataTransfer.files, setFiles, setError);
        }}
        onClick={() => fileRef.current?.click()}
        data-testid="wizard-upload-dropzone"
      >
        <Upload size={24} className="mx-auto text-indigo-600 mb-2" />
        <div className="text-[13px] font-medium text-slate-900">Drag & drop files here</div>
        <div className="text-[11px] text-slate-500 mt-1">{FILE_UPLOAD_FORMATS_LABEL} · multiple files</div>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden"
          onChange={(e) => {
            applySelectedUploadFiles(e.target.files, setFiles, setError);
            e.target.value = "";
          }} />
      </button>
      {files.length > 0 && (
        <ul className="text-[12px] text-slate-700 space-y-1 max-h-28 overflow-y-auto rounded-lg border border-slate-100 p-2">
          {files.map((f) => (
            <li key={f.name} className="min-w-0 truncate" title={`${f.name} · ${formatBytes(f.size)}`}>
              {f.name} · {formatBytes(f.size)}
            </li>
          ))}
        </ul>
      )}
      {uploadResults.length > 0 && (
        <div className="text-[11px] text-slate-500">
          {uploadResults.filter((r) => r.status === "ready").length} file(s) processed in a previous session.
        </div>
      )}
    </div>
  );
}

WizardStepUpload.propTypes = {
  name: PropTypes.string.isRequired,
  sourceDetail: sourceDetailPropType,
  dragOver: PropTypes.bool.isRequired,
  setDragOver: PropTypes.func.isRequired,
  setFiles: PropTypes.func.isRequired,
  setError: PropTypes.func,
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  fileRef: PropTypes.shape({ current: PropTypes.any }),
  uploadResults: PropTypes.arrayOf(uploadResultPropType).isRequired,
};

function WizardStepProcess({
  processingFlags, uploadResults, sourceDetail, syncResult, busy, onRetrySync,
}) {
  const syncInfo = describeSync(syncResult, sourceDetail);
  const readyCount = uploadResults.filter((r) => r.status === "ready").length;
  const uploadHint = uploadResults.length
    ? `${readyCount} file(s) ready`
    : `${sourceDetail?.document_count || 0} document(s)`;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-4 bg-slate-50/50">
      <StepIndicator done={processingFlags.uploaded} label="Documents uploaded" hint={uploadHint} />
      <StepIndicator done={processingFlags.chunked} label="Chunking complete" />
      <StepIndicator done={processingFlags.embedded} label="Embeddings generated" />
      <StepIndicator done={processingFlags.indexed} label="Knowledge indexed in Sellyx Brain" />
      <StepIndicator
        done={syncInfo.ok || processingFlags.synced}
        active={busy && !processingFlags.synced}
        label={syncInfo.label}
        hint={syncInfo.hint}
      />
      {syncResult?.status === "failed" && (
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onRetrySync} disabled={busy}>
          <RefreshCw size={12} /> Retry sync
        </Button>
      )}
      {syncResult?.status === "skipped" && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
          Knowledge is searchable in Sellyx. OpenClaw workspace sync runs when the gateway connects.
        </p>
      )}
    </div>
  );
}

WizardStepProcess.propTypes = {
  processingFlags: processingFlagsPropType.isRequired,
  uploadResults: PropTypes.arrayOf(uploadResultPropType).isRequired,
  sourceDetail: sourceDetailPropType,
  syncResult: syncResultPropType,
  busy: PropTypes.bool.isRequired,
  onRetrySync: PropTypes.func.isRequired,
};

function WizardStepSuccess({ sourceDetail }) {
  return (
    <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center gap-2 text-emerald-800 font-semibold text-[14px]">
        <Sparkles size={18} /> Knowledge source ready
      </div>
      <p className="text-[12px] text-emerald-900/80">
        Your documents are indexed and automatically available to all agents.
      </p>
      <dl className="grid grid-cols-2 gap-2 text-[12px]">
        <div className="min-w-0 col-span-2 sm:col-span-1">
          <dt className="text-slate-500">Name</dt>
          <dd className="font-medium text-slate-900 break-all" title={sourceDetail.name}>{sourceDetail.name}</dd>
        </div>
        <div className="min-w-0"><dt className="text-slate-500">Documents</dt><dd className="font-medium text-slate-900">{sourceDetail.document_count}</dd></div>
        <div className="min-w-0"><dt className="text-slate-500">Chunks</dt><dd className="font-medium text-slate-900">{sourceDetail.chunk_count}</dd></div>
        <div className="min-w-0"><dt className="text-slate-500">Agents</dt><dd className="font-medium text-slate-900">All agents</dd></div>
        <div className="col-span-2">
          <dt className="text-slate-500">Sync status</dt>
          <dd className="font-medium text-slate-900">{syncStatusLabel(sourceDetail.openclaw_sync_status)}</dd>
        </div>
      </dl>
    </div>
  );
}

WizardStepSuccess.propTypes = {
  sourceDetail: sourceDetailPropType.isRequired,
};

function WizardFooter({
  step, busy, name, canProceedStep2, canProceedStep3,
  onCancel, onCreate, onGoBack, onSaveAndExit, onUpload, onRetrySync,
  onFinishSetup, onFinish,
}) {
  if (step === 1) {
    return (
      <>
        <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button className="w-full sm:w-auto h-10 sm:h-9" onClick={onCreate} disabled={busy || !name.trim()} data-testid="wizard-next-create">
          {busy ? <Loader2 className="animate-spin" size={14} /> : "Next"}
        </Button>
      </>
    );
  }
  if (step === 2) {
    return (
      <>
        <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={onGoBack} disabled={busy} data-testid="wizard-back-upload">
          Back
        </Button>
        <div className="flex flex-col-reverse sm:flex-row shrink-0 gap-2 w-full sm:w-auto">
          <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={onSaveAndExit} disabled={busy}>Save & exit</Button>
          <Button className="w-full sm:w-auto h-10 sm:h-9" onClick={onUpload} disabled={busy || !canProceedStep2} data-testid="wizard-upload-next">
            {busy ? <Loader2 className="animate-spin" size={14} /> : "Upload & continue"}
          </Button>
        </div>
      </>
    );
  }
  if (step === 3) {
    return (
      <>
        <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={onGoBack} disabled={busy} data-testid="wizard-back-process">
          Back
        </Button>
        <div className="flex flex-col-reverse sm:flex-row shrink-0 gap-2 w-full sm:w-auto">
          <Button variant="outline" className="w-full sm:w-auto h-10 sm:h-9" onClick={onRetrySync} disabled={busy}>
            <RefreshCw size={12} /> Retry sync
          </Button>
          <Button
            className="w-full sm:w-auto h-10 sm:h-9"
            onClick={onFinishSetup}
            disabled={!canProceedStep3 || busy}
            data-testid="wizard-finish"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : "Finish"}
          </Button>
        </div>
      </>
    );
  }
  if (step === 4) {
    return (
      <Button onClick={onFinish} data-testid="wizard-done" className="w-full sm:w-auto sm:ml-auto h-10 sm:h-9">
        Done
      </Button>
    );
  }
  return null;
}

WizardFooter.propTypes = {
  step: PropTypes.number.isRequired,
  busy: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  canProceedStep2: PropTypes.bool.isRequired,
  canProceedStep3: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onSaveAndExit: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onRetrySync: PropTypes.func.isRequired,
  onFinishSetup: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
};

export function KnowledgeSourceSetupWizard({ open, onOpenChange, sources = [], onComplete, initialSourceId = null }) {
  const [step, setStep] = useState(1);
  const [sourceId, setSourceId] = useState(null);
  const [sourceDetail, setSourceDetail] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [processingFlags, setProcessingFlags] = useState({
    uploaded: false, chunked: false, embedded: false, indexed: false, synced: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const isSavingAndExiting = useRef(false);

  const showSuccess = step === 4;

  const loadSource = useCallback(async (id) => {
    if (!id) return null;
    const d = await brainApi.getCustomSource(id);
    setSourceDetail(d);
    return d;
  }, []);

  const persistWizardSession = useCallback(async (id, stepNum, { forceUpload = false, completed = false, saved = false } = {}) => {
    if (!id) return;
    await brainApi.saveWizardSession(id, {
      step: stepNum,
      force_upload: forceUpload,
      completed,
      saved,
    });
  }, []);

  const initWizard = useCallback(async () => {
    setError(null);
    setUploadResults([]);
    setSyncResult(null);
    setFiles([]);

    const pickId = initialSourceId ?? null;

    if (!pickId) {
      clearProgress();
      setSourceId(null);
      setSourceDetail(null);
      setName("");
      setDescription("");
      setStep(1);
      logWizard("Wizard started fresh (no source)");
      return;
    }

    const detail = await loadSource(pickId);
    if (!detail) {
      setStep(1);
      return;
    }

    setSourceId(detail.id);
    setName(detail.name);
    setDescription(detail.metadata?.description || "");

    const startStep = computeWizardStep(detail);
    logWizardState("Calculated wizard step", detail, startStep);
    setProcessingFlags(buildProcessingFlags(detail));
    setSyncResult(buildSyncResultFromDetail(detail));
    setStep(startStep);
    logWizard("Wizard initialized", { sourceId: detail.id, step: startStep });
  }, [initialSourceId, loadSource]);

  useEffect(() => {
    if (!open) return;
    isSavingAndExiting.current = false;
    initWizard().catch((e) => {
      setError(e?.message || "Could not initialize wizard.");
    });
  }, [open, initWizard]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    logWizard("Datasource create requested", { name: name.trim() });
    try {
      if (sourceId) {
        if (name.trim() !== sourceDetail?.name) {
          const updated = await brainApi.renameCustomSource(sourceId, { name: name.trim() });
          setSourceDetail(updated);
        }
        await persistWizardSession(sourceId, 2, { forceUpload: true });
        setStep(2);
        return;
      }
      const src = await brainApi.createCustomSource({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      logWizard("Datasource created", { sourceId: src.id });
      setSourceId(src.id);
      setSourceDetail(src);
      setStep(2);
    } catch (e) {
      logWizard("Datasource creation failed", { message: e.message });
      setError(e.message || "Unable to create knowledge source.");
    } finally {
      setBusy(false);
    }
  };

  const applySuccessfulUpload = async (res, sid) => {
    const sync = res.openclaw_sync;
    logWizard("Documents uploaded and processed", {
      count: (res.results || []).filter((r) => r.status === "ready").length,
      syncStatus: sync?.status,
      syncMode: sync?.sync_mode,
    });
    const syncOk = !sync || ["completed", "skipped"].includes(sync.status);
    setProcessingFlags({
      uploaded: true,
      chunked: true,
      embedded: true,
      indexed: true,
      synced: syncOk,
    });
    setFiles([]);
    try {
      await persistWizardSession(sid, 3);
    } catch (persistErr) {
      logWizard("Wizard session persist failed after upload", {
        message: persistErr.message,
      });
    }
    setStep(3);
  };

  const handleUpload = async () => {
    if (!sourceId || files.length === 0) return;
    setBusy(true);
    setError(null);
    logWizard("Documents upload started", { sourceId, fileCount: files.length });
    try {
      const res = await brainApi.uploadCustomSource(sourceId, files);
      const ok = (res.results || []).filter((r) => r.status === "ready");
      const failed = (res.results || []).filter((r) => r.status === "failed");
      setUploadResults(res.results || []);
      setSyncResult(res.openclaw_sync || null);
      setSourceDetail(res.source || null);

      if (ok.length) {
        await applySuccessfulUpload(res, sourceId);
      }
      if (failed.length) {
        const msg = failed.map((r) => r.error || r.filename).filter(Boolean).join("; ")
          || FILE_UPLOAD_REJECT_MESSAGE;
        setError(msg);
      }
    } catch (e) {
      logWizard("Upload failed", { message: e.message });
      setError(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRetrySync = async () => {
    if (!sourceId) return;
    setBusy(true);
    setError(null);
    logWizard("OpenClaw sync retry", { sourceId });
    try {
      const res = await brainApi.syncCustomSource(sourceId);
      setSyncResult(res.openclaw_sync);
      setSourceDetail(res.source || sourceDetail);
      setProcessingFlags((f) => ({
        ...f,
        synced: ["completed", "skipped"].includes(res.openclaw_sync?.status) || res.openclaw_sync?.sync_mode === "sellix_rag",
      }));
      await loadSource(sourceId);
    } catch (e) {
      setError(e.message || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const handleFinishSetup = async () => {
    if (!sourceId) return;
    setBusy(true);
    setError(null);
    logWizard("Finish setup — auto-assign all agents", { sourceId });
    try {
      await brainApi.assignCustomSource(sourceId, { agent_ids: ["all"] });
      await persistWizardSession(sourceId, 4, { completed: true });
      const detail = await loadSource(sourceId);
      setSourceDetail(detail);
      setStep(4);
      logWizard("Datasource setup completed", { sourceId });
    } catch (e) {
      setError(e.message || "Could not complete knowledge source setup");
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async () => {
    if (sourceId) {
      try {
        await persistWizardSession(sourceId, 4, { completed: true });
      } catch {
        /* non-blocking on done screen */
      }
    }
    onOpenChange(false);
    if (onComplete) onComplete();
  };

  const goBack = async () => {
    if (step <= 1) return;
    setError(null);
    const prev = step - 1;
    if (sourceId) {
      try {
        await persistWizardSession(sourceId, prev, { forceUpload: prev === 2 });
      } catch (e) {
        setError(e.message || "Could not save progress");
        return;
      }
    }
    setStep(prev);
    logWizard("Wizard step back", { from: step, to: prev, sourceId });
  };

  const handleCancel = async () => {
    setBusy(true);
    setError(null);
    const cancellingId = sourceId;
    logWizard("Wizard cancel requested", {
      sourceId: cancellingId,
      step,
      documentCount: sourceDetail?.document_count ?? 0,
      status: sourceDetail?.status,
    });
    try {
      if (cancellingId) {
        try {
          const result = await brainApi.cancelWizardSession(cancellingId);
          logWizard("Wizard cancel API ok", {
            sourceId: cancellingId,
            sourceDeleted: result?.source_deleted,
            discarded: result?.discarded,
          });
        } catch (e) {
          // Draft may already be gone after deleting all uploaded files.
          if (e?.status !== 404) throw e;
          logWizard("Wizard cancel — draft already removed", { sourceId: cancellingId });
        }
        clearProgress(cancellingId);
      } else {
        clearProgress();
      }
      setSourceId(null);
      setSourceDetail(null);
      setName("");
      setDescription("");
      setFiles([]);
      setUploadResults([]);
      setSyncResult(null);
      setProcessingFlags({
        uploaded: false,
        chunked: false,
        embedded: false,
        indexed: false,
        synced: false,
      });
      setStep(1);
      onOpenChange(false);
      if (onComplete) await onComplete();
      logWizard("Wizard cancelled — UI + draft cleared", { sourceId: cancellingId });
    } catch (e) {
      logWizard("Wizard cancel failed", { message: e.message });
      setError(e.message || "Could not discard upload session.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAndExit = async () => {
    if (!sourceId || step >= 4) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      isSavingAndExiting.current = true;
      // Save & exit must not upload — only "Upload & continue" processes files.
      // Selected File objects cannot be restored after exit; clear the local queue.
      if (step === 2 && files.length > 0) {
        setFiles([]);
      }
      await persistWizardSession(sourceId, step, { forceUpload: step === 2, saved: true });
      logWizard("Wizard closed — progress saved", { sourceId, step });
      onOpenChange(false);
      if (onComplete) onComplete();
    } catch (e) {
      isSavingAndExiting.current = false;
      setError(e.message || "Could not save progress.");
    } finally {
      setBusy(false);
    }
  };

  const runCancel = () => {
    handleCancel().catch((e) => setError(e?.message || "Could not discard upload session."));
  };

  const runGoBack = () => {
    goBack().catch((e) => setError(e?.message || "Could not save progress"));
  };

  const runSaveAndExit = () => {
    handleSaveAndExit().catch((e) => setError(e?.message || "Could not save progress."));
  };

  const handleClose = (nextOpen) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (step === 4 || isSavingAndExiting.current) {
      onOpenChange(false);
      return;
    }
    runCancel();
  };

  const canProceedStep2 = files.length > 0;
  const canProceedStep3 = processingFlags.indexed;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="knowledge-setup-wizard">
        <DialogHeader>
          <DialogTitle>{showSuccess ? "Knowledge source ready" : "Set up knowledge source"}</DialogTitle>
          <DialogDescription>
            {showSuccess
              ? "Your documents are indexed and available to all agents."
              : "Upload documents once — we'll chunk, embed, and make them available to all agents."}
          </DialogDescription>
        </DialogHeader>

        <ProgressHeader step={step} showSuccess={showSuccess} />

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex gap-2">
            <AlertCircle size={16} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <WizardStepCreate
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
          />
        )}

        {step === 2 && (
          <WizardStepUpload
            name={name}
            sourceDetail={sourceDetail}
            dragOver={dragOver}
            setDragOver={setDragOver}
            setFiles={setFiles}
            setError={setError}
            files={files}
            fileRef={fileRef}
            uploadResults={uploadResults}
          />
        )}

        {step === 3 && (
          <WizardStepProcess
            processingFlags={processingFlags}
            uploadResults={uploadResults}
            sourceDetail={sourceDetail}
            syncResult={syncResult}
            busy={busy}
            onRetrySync={handleRetrySync}
          />
        )}

        {step === 4 && sourceDetail && (
          <WizardStepSuccess sourceDetail={sourceDetail} />
        )}

        <div className="flex w-full flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-2" data-testid="wizard-footer">
          <WizardFooter
            step={step}
            busy={busy}
            name={name}
            canProceedStep2={canProceedStep2}
            canProceedStep3={canProceedStep3}
            onCancel={runCancel}
            onCreate={handleCreate}
            onGoBack={runGoBack}
            onSaveAndExit={runSaveAndExit}
            onUpload={handleUpload}
            onRetrySync={handleRetrySync}
            onFinishSetup={handleFinishSetup}
            onFinish={handleFinish}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

KnowledgeSourceSetupWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(customSourceSummaryPropType),
  onComplete: PropTypes.func,
  initialSourceId: PropTypes.string,
};

export default KnowledgeSourceSetupWizard;
